export const formatSlideContent = (content, columnHeaders = null) => {
    if (!content) return "";
    
    if (typeof content === 'string') {
        const trimmed = content.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
            try {
                const parsed = JSON.parse(content);
                if (parsed) {
                    content = parsed;
                }
            } catch (e) {}
        }
    }

    if (typeof content === 'string') return content;

    // 0. Handle before_after structure when content is an object rather than an array
    if (content && typeof content === 'object' && !Array.isArray(content) && ('before' in content || 'after' in content)) {
        let parts = [];
        if (content.title) parts.push(`### ${content.title}`);
        if (content.before) {
            const bHeader = content.before.header || "Before";
            parts.push(`**${bHeader}**`);
            const bContent = content.before.content || "";
            if (Array.isArray(bContent)) {
                parts.push(bContent.map(sub => { const s = typeof sub === 'string' ? sub : JSON.stringify(sub); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join('\n'));
            } else {
                const lines = bContent.split('\n').map(l => l.trim()).filter(Boolean);
                parts.push(lines.map(line => line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') ? line : `- ${line}`).join('\n'));
            }
        }
        if (content.after) {
            const aHeader = content.after.header || "After";
            parts.push(`\n**${aHeader}**`);
            const aContent = content.after.content || "";
            if (Array.isArray(aContent)) {
                parts.push(aContent.map(sub => { const s = typeof sub === 'string' ? sub : JSON.stringify(sub); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join('\n'));
            } else {
                const lines = aContent.split('\n').map(l => l.trim()).filter(Boolean);
                parts.push(lines.map(line => line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') ? line : `- ${line}`).join('\n'));
            }
        }
        return parts.join('\n');
    }

    if (Array.isArray(content)) {
        if (content.length === 0) return "";

        if (content.every(item => typeof item === 'string')) {
            return content.map(item => item.startsWith('- ') || item.startsWith('* ') || item.startsWith('• ') ? item : `- ${item}`).join('\n');
        }

        // 1. Check if it's a before_after layout structure
        const first = content[0];
        if (first && typeof first === 'object' && ('before' in first || 'after' in first)) {
            return content.map(item => {
                let parts = [];
                if (item.title) parts.push(`### ${item.title}`);
                if (item.before) {
                    const bHeader = item.before.header || "Before";
                    parts.push(`**${bHeader}**`);
                    const bContent = item.before.content || "";
                    if (Array.isArray(bContent)) {
                        parts.push(bContent.map(sub => { const s = typeof sub === 'string' ? sub : JSON.stringify(sub); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join('\n'));
                    } else {
                        const lines = bContent.split('\n').map(l => l.trim()).filter(Boolean);
                        parts.push(lines.map(line => line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') ? line : `- ${line}`).join('\n'));
                    }
                }
                if (item.after) {
                    const aHeader = item.after.header || "After";
                    parts.push(`\n**${aHeader}**`);
                    const aContent = item.after.content || "";
                    if (Array.isArray(aContent)) {
                        parts.push(aContent.map(sub => { const s = typeof sub === 'string' ? sub : JSON.stringify(sub); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join('\n'));
                    } else {
                        const lines = aContent.split('\n').map(l => l.trim()).filter(Boolean);
                        parts.push(lines.map(line => line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') ? line : `- ${line}`).join('\n'));
                    }
                }
                return parts.join('\n');
            }).join('\n\n');
        }

        // 2. Check if it's a table layout structure (has keys or columns)
        if (first && typeof first === 'object') {
            let headers = columnHeaders;
            if (!headers || !Array.isArray(headers)) {
                headers = Object.keys(first).filter(k => k !== 'id' && k !== 'index' && k !== 'key');
            }
            if (headers && headers.length > 0) {
                // Ensure this is not a false-positive before_after or simple bullet object list
                const isComplexTable = headers.some(h => h.toLowerCase().includes('component') || h.toLowerCase().includes('technology') || h.toLowerCase().includes('responsibility') || h.toLowerCase().includes('activity') || h.toLowerCase().includes('sl no') || h.toLowerCase().includes('r (') || h.toLowerCase().includes('a (') || h.toLowerCase().includes('c (') || h.toLowerCase().includes('i ('));
                const isBeforeAfter = headers.includes('before') || headers.includes('after');
                
                if (isComplexTable && !isBeforeAfter) {
                    let mdTable = [];
                    // Header row
                    mdTable.push('| ' + headers.join(' | ') + ' |');
                    // Separator row
                    mdTable.push('| ' + headers.map(() => '---').join(' | ') + ' |');
                    // Data rows
                    content.forEach(row => {
                        const rowVals = headers.map(h => {
                            const val = row[h] !== undefined ? row[h] : '';
                            return typeof val === 'object' ? JSON.stringify(val) : String(val).replace(/\n/g, '<br/>');
                        });
                        mdTable.push('| ' + rowVals.join(' | ') + ' |');
                    });
                    return mdTable.join('\n');
                }
            }
        }

        return content.map(item => {
            if (typeof item === 'string') {
                return item.startsWith('- ') || item.startsWith('* ') || item.startsWith('• ') ? item : `- ${item}`;
            }
            if (typeof item === 'object' && item !== null) {
                if (item.headline || item.subtext) {
                    const headline = item.headline || "";
                    const subtext = item.subtext || "";
                    return headline && subtext ? `**${headline}**: ${subtext}` : (headline || subtext);
                }
                if (item.Feature || item.Description) {
                    const feature = item.Feature || "";
                    const desc = item.Description || "";
                    return feature && desc ? `**${feature}**: ${desc}` : (feature || desc);
                }
                if (item.Mandate) {
                    const mandate = item.Mandate || "";
                    const desc = item.Description || "";
                    return mandate && desc ? `**${mandate}**: ${desc}` : (mandate || desc);
                }
                if (item.Component) {
                    const component = item.Component || "";
                    const desc = item.Description || "";
                    return component && desc ? `**${component}**: ${desc}` : (component || desc);
                }
                if (item.top_label || item.header || item.content) {
                    let parts = [];
                    if (item.top_label) parts.push(`### ${item.top_label}`);
                    if (item.header) parts.push(`**${item.header}**`);
                    if (item.content) {
                        if (Array.isArray(item.content)) {
                            parts.push(item.content.map(sub => { const s = typeof sub === 'string' ? sub : JSON.stringify(sub); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join('\n'));
                        } else {
                            const sContent = typeof item.content === 'string' ? item.content : JSON.stringify(item.content);
                            parts.push(sContent.startsWith('- ') || sContent.startsWith('* ') || sContent.startsWith('• ') ? sContent : `- ${sContent}`);
                        }
                    }
                    return parts.join('\n');
                }

                const keys = Object.keys(item).filter(k => k !== 'id' && k !== 'index');
                if (keys.length === 2) {
                    const val1 = item[keys[0]];
                    const val2 = item[keys[1]];
                    if (Array.isArray(val2)) {
                        return `**${val1}**:\n${val2.map(sub => `  - ${sub}`).join('\n')}`;
                    }
                    return `**${val1}**: ${val2}`;
                }

                return Object.entries(item)
                    .map(([key, val]) => {
                        if (Array.isArray(val)) {
                            return `**${key}**:\n${val.map(sub => `  - ${sub}`).join('\n')}`;
                        }
                        if (typeof val === 'object' && val !== null) {
                            if (val.header || val.content) {
                                let subParts = [];
                                if (val.header) subParts.push(`**${val.header}**`);
                                if (val.content) {
                                    if (Array.isArray(val.content)) {
                                        subParts.push(val.content.map(sub => `  - ${sub}`).join('\n'));
                                    } else {
                                        subParts.push(val.content.split('\n').map(sub => `  - ${sub}`).join('\n'));
                                    }
                                }
                                return subParts.join('\n');
                            }
                            return `**${key}**:\n` + Object.entries(val).map(([k, v]) => `  - **${k}**: ${v}`).join('\n');
                        }
                        return `**${key}**: ${val}`;
                    })
                    .join('\n');
            }
            return '';
        }).filter(Boolean).join('\n\n');
    }

    if (typeof content === 'object') {
        if (content.text) {
            let res = content.text;
            if (content.bullets && Array.isArray(content.bullets)) {
                res += "\n\n" + content.bullets.map(b => { const s = typeof b === 'string' ? b : JSON.stringify(b); return s.startsWith('- ') || s.startsWith('* ') || s.startsWith('• ') ? s : `- ${s}`; }).join("\n");
            }
            return res;
        }
        return Object.entries(content)
            .map(([key, val]) => {
                if (Array.isArray(val)) {
                    return `**${key}**:\n${val.map(sub => `  - ${sub}`).join('\n')}`;
                }
                if (typeof val === 'object' && val !== null) {
                    if (val.header || val.content) {
                        let subParts = [];
                        if (val.header) subParts.push(`**${val.header}**`);
                        if (val.content) {
                            if (Array.isArray(val.content)) {
                                subParts.push(val.content.map(sub => `  - ${sub}`).join('\n'));
                            } else {
                                subParts.push(val.content.split('\n').map(sub => `  - ${sub}`).join('\n'));
                            }
                        }
                        return subParts.join('\n');
                    }
                    return `**${key}**:\n` + Object.entries(val).map(([k, v]) => `  - **${k}**: ${v}`).join('\n');
                }
                return `**${key}**: ${val}`;
            })
            .join('\n');
    }

    return "";
};
